import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, EyeOff, ArrowRight, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { VentureCard, VentureCardContent } from '@/components/ui/venture-card';
import { useRole } from '@/contexts/RoleContext';

type OnboardingStep = 'password' | 'done';

export default function ClientOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState<OnboardingStep>('password');
  const { clientId } = useRole();

  // Password step state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Complete onboarding helper
  const completeOnboarding = useCallback(async () => {
    if (clientId) {
      await supabase
        .from('clients')
        .update({ onboarding_complete: true } as any)
        .eq('id', clientId);
    }
    localStorage.setItem('onboarding_complete', 'true');
    toast.success('Account setup complete. Welcome!');
    navigate('/dashboard');
  }, [clientId, navigate]);

  // Handle password form submit
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      toast.success('Password updated successfully');
      await completeOnboarding();
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step indicator rendering
  const renderStepIndicator = () => {
    const steps = [
      { key: 'signin', label: 'Signed in' },
      { key: 'password', label: 'Set password' },
      { key: 'done', label: 'Done' },
    ];

    const currentIndex = step === 'password' ? 1 : 2;

    return (
      <div className="flex items-center gap-2 text-sm">
        {steps.map((s, i) => (
          <div key={s.key} className="flex items-center gap-2">
            {i > 0 && <div className="flex-1 h-px bg-border w-4 min-w-[16px]" />}
            <div className={`flex items-center gap-1.5 whitespace-nowrap ${
              i < currentIndex
                ? 'text-primary'
                : i === currentIndex
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground'
            }`}>
              {i < currentIndex ? (
                <CheckCircle2 className="w-4 h-4 shrink-0" />
              ) : i === currentIndex ? (
                <div className="w-4 h-4 rounded-full border-2 border-primary flex items-center justify-center shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                </div>
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-border shrink-0" />
              )}
              <span>{s.label}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Subtitle per step
  const getSubtitle = () => {
    if (step === 'password') return 'Set a new password to secure your account';
    return 'Your account is ready';
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <VentureCard hover={false} className="p-8">
          <VentureCardContent className="space-y-8">
            {/* Header */}
            <div className="flex flex-col items-center text-center space-y-4">
              <Logo size="lg" />
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">Welcome to Venture Hills</h1>
                <p className="text-muted-foreground text-sm">{getSubtitle()}</p>
              </div>
            </div>

            {/* Steps indicator */}
            {renderStepIndicator()}

            {/* Step content */}
            <AnimatePresence mode="wait">
              {step === 'password' && (
                <motion.form
                  key="password-step"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  onSubmit={handlePasswordSubmit}
                  className="space-y-4"
                >
                  {error && (
                    <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type={showNew ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 bg-surface border border-border rounded-lg text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                        placeholder="Min. 8 characters"
                        required
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setShowNew(!showNew)}
                      >
                        {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Confirm Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type={showConfirm ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 bg-surface border border-border rounded-lg text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                        placeholder="Re-enter your password"
                        required
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setShowConfirm(!showConfirm)}
                      >
                        {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    variant="gradient"
                    size="xl"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? 'Updating password...' : (
                      <>
                        Continue
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </motion.form>
              )}

            </AnimatePresence>
          </VentureCardContent>
        </VentureCard>
      </motion.div>
    </div>
  );
}
